import { fetcherConfig } from '../lib/api-fetcher';
import { useState } from 'react';
import { createFileRoute, Outlet, useNavigate, useLocation, redirect } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { TwoFactorStepUpDialog } from '../components/ui/TwoFactorStepUpDialog';
import { getSession, clearSession } from '../lib/session';
import { sessionQueryKey } from '../lib/query-client';
import { createAuthResource } from '@repo/api-client';

const authResource = createAuthResource(fetcherConfig);

const DashboardIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="8" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const SettingsIcon = () => <Settings className="w-5 h-5" />;

const ReportsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="8" width="2.5" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="5.75" y="5" width="2.5" height="8" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="10.5" y="1" width="2.5" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const MobileAppsIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
    <rect x="3" y="1" width="8" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
    <line x1="5.5" y1="11" x2="8.5" y2="11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    if (!session) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
    if (session.requiresPasswordReset) {
      throw redirect({
        to: '/force-password-change',
      });
    }
  },
  component: AuthLayout,
});

function AuthLayout() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pathname: currentPath } = useLocation();
  const { data: session } = useQuery({
    queryKey: sessionQueryKey,
    queryFn: getSession,
    staleTime: Infinity,
  });

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  if (!session || session.requiresPasswordReset) {
    return null;
  }

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirmed = async () => {
    setLogoutConfirmOpen(false);
    try {
      await authResource.logout(session.refreshToken);
    } catch (err) {
      // Local session is cleared in `finally` regardless, so a failed remote
      // logout call doesn't block the user — just log it for debugging.
      console.error('Failed to log out on server', err);
    } finally {
      clearSession();
      qc.clear();
      void navigate({ to: '/login' });
    }
  };

  const crmItems = [
    ...(['group_admin', 'company_admin', 'staff'].includes(session.user.type)
      ? [
          {
            id: 'crm-leads',
            label: 'CRM Leads',
            href: '/crm/leads',
            active: currentPath.startsWith('/crm'),
          },
        ]
      : []),
    {
      id: 'customers',
      label: 'Customers',
      href: '/customers',
      active: currentPath.startsWith('/customers'),
    },
    ...(session.user.type === 'company_admin' ||
    session.user.type === 'group_admin' ||
    session.permissions?.includes('chitfunds.surety.read')
      ? [
          {
            id: 'surety',
            label: 'Surety (Jamin/Guarantor)',
            href: '/surety',
            active: currentPath.startsWith('/surety'),
          },
        ]
      : []),
  ];

  const chitCoreItems = [
    {
      id: 'schemes',
      label: 'Chit Groups',
      href: '/schemes',
      active: currentPath.startsWith('/schemes') && !currentPath.startsWith('/schemes/transfers') && !currentPath.startsWith('/schemes/estimate'),
    },
    {
      id: 'chit-estimate',
      label: 'Chit Estimate',
      href: '/schemes/estimate',
      active: currentPath.startsWith('/schemes/estimate'),
    },
    {
      id: 'auctions',
      label: 'Auction Dashboard',
      href: '/auctions',
      active: currentPath.startsWith('/auctions'),
    },
    {
      id: 'name-transfers',
      label: 'Name Transfers',
      href: '/schemes/transfers',
      active: currentPath.startsWith('/schemes/transfers'),
    },
    {
      id: 'pidi-schemes',
      label: 'Pidi Chits',
      href: '/pidi-schemes',
      active: currentPath.startsWith('/pidi-schemes'),
    },
  ];

  const financeItems = [
    {
      id: 'operations',
      label: 'Operations & Tasks',
      href: '/operations',
      active: currentPath.startsWith('/operations'),
    },
    {
      id: 'approvals',
      label: 'Pending Approvals',
      href: '/approvals',
      active: currentPath.startsWith('/approvals'),
    },
    {
      id: 'instructions',
      label: 'Instruction Queue',
      href: '/instructions',
      active: currentPath.startsWith('/instructions'),
    },
    ...(session.permissions?.includes('chitfunds.payout.read')
      ? [
          {
            id: 'payouts',
            label: 'Prize Payouts',
            href: '/payouts',
            active: currentPath.startsWith('/payouts'),
          },
        ]
      : []),

    ...(session.permissions?.includes('chitfunds.foreclosure.read') || session.user.type === 'company_admin' || session.user.type === 'group_admin'
      ? [
          {
            id: 'foreclosure',
            label: 'Subscription Exits',
            href: '/foreclosure',
            active: currentPath.startsWith('/foreclosure'),
          },
        ]
      : []),
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin'
      ? [
          {
            id: 'accounts',
            label: 'Ledger Accounts',
            href: '/accounting/accounts',
            active: currentPath.startsWith('/accounting/accounts'),
          },
          {
            id: 'general-transactions',
            label: 'General Transactions',
            href: '/accounting/general-transactions',
            active: currentPath.startsWith('/accounting/general-transactions'),
          },
          {
            id: 'commission-register',
            label: 'Commission Register',
            href: '/accounting/commission',
            active: currentPath.startsWith('/accounting/commission'),
          },
        ]
      : []),
    ...(session.permissions?.includes('chitfunds.tax.read')
      ? [
          {
            id: 'tax',
            label: 'Tax Registers',
            href: '/tax',
            active: currentPath.startsWith('/tax'),
          },
        ]
      : []),
    {
      id: 'finance-receipts',
      label: 'Chit Receipt',
      href: '/finance/receipts',
      active: currentPath.startsWith('/finance/receipts'),
    },
    {
      id: 'finance-receipt-vouchers',
      label: 'Chit Receipt Voucher',
      href: '/finance/receipt-vouchers',
      active: currentPath.startsWith('/finance/receipt-vouchers'),
    },
    {
      id: 'cheque-register',
      label: 'Cheque Register',
      href: '/finance/cheque-register',
      active: currentPath.startsWith('/finance/cheque-register'),
    },
    {
      id: 'late-fee-settlement',
      label: 'Late Fee Settlement',
      href: '/overdue',
      active: currentPath.startsWith('/overdue'),
    },
    {
      id: 'finance-advances',
      label: 'Chit Advances & Interest',
      href: '/finance/advances',
      active: currentPath.startsWith('/finance/advances'),
    },
    {
      id: 'finance-running-receipts',
      label: 'Running Receipts',
      href: '/finance/running-receipts',
      active: currentPath.startsWith('/finance/running-receipts'),
    },
    {
      id: 'finance-group-deposits',
      label: 'Group Deposits',
      href: '/finance/group-deposits',
      active: currentPath.startsWith('/finance/group-deposits'),
    },
    {
      id: 'finance-decrees',
      label: 'Legal Decrees',
      href: '/finance/decrees',
      active: currentPath.startsWith('/finance/decrees'),
    },
    {
      id: 'finance-chit-act-fees',
      label: 'Chit Act Fees',
      href: '/finance/chit-act-fees',
      active: currentPath.startsWith('/finance/chit-act-fees'),
    },
    {
      id: 'finance-write-offs',
      label: 'Bad Debits',
      href: '/finance/write-offs',
      active: currentPath.startsWith('/finance/write-offs'),
    },
    {
      id: 'finance-cash-vault',
      label: 'Cash Vault',
      href: '/finance/cash-vault',
      active: currentPath.startsWith('/finance/cash-vault'),
    },
    {
      id: 'finance-handovers',
      label: 'Cash Handovers',
      href: '/finance/handovers',
      active: currentPath.startsWith('/finance/handovers'),
    },
  ];

  const reportItems = [
    {
      id: 'all-reports',
      label: 'All Reports',
      href: '/reports',
      active: currentPath === '/reports',
    },
    {
      id: 'report-daybook',
      label: 'Daybook',
      href: '/reports/daybook',
      active: currentPath.startsWith('/reports/daybook'),
    },
    {
      id: 'report-subscriber-ledger',
      label: 'Subscriber Ledger',
      href: '/reports/subscriber-ledger',
      active: currentPath.startsWith('/reports/subscriber-ledger'),
    },
    {
      id: 'report-arrears',
      label: 'Arrears Dashboard',
      href: '/reports/arrears',
      active: currentPath.startsWith('/reports/arrears'),
    },
    {
      id: 'report-pl',
      label: 'Profit & Loss',
      href: '/reports/financial/pnl',
      active: currentPath.startsWith('/reports/financial/pnl'),
    },
    {
      id: 'report-balance-sheet',
      label: 'Balance Sheet',
      href: '/reports/financial/balance-sheet',
      active: currentPath.startsWith('/reports/financial/balance-sheet'),
    },
    {
      id: 'report-trial-balance',
      label: 'Trial Balance',
      href: '/reports/financial/trial-balance',
      active: currentPath.startsWith('/reports/financial/trial-balance'),
    },
    {
      id: 'report-operational-collections',
      label: 'Collection Performance',
      href: '/reports/operational/collections',
      active: currentPath.startsWith('/reports/operational/collections'),
    },
    {
      id: 'report-operational-staff-activity',
      label: 'Staff Activity',
      href: '/reports/operational/staff-activity',
      active: currentPath.startsWith('/reports/operational/staff-activity'),
    },
  ];

  const configItems = [
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.permissions?.includes('user.read')
      ? [
          {
            id: 'users',
            label: 'Users',
            href: '/users',
            active: currentPath.startsWith('/users'),
          },
        ]
      : []),
    {
      id: 'organization',
      label: 'Organization',
      href: '/setup/company',
      active: currentPath === '/setup/company',
    },
    {
      id: 'roles',
      label: 'Roles',
      href: '/settings/roles',
      active: currentPath === '/settings/roles',
    },
    {
      id: 'branches',
      label: 'Branches',
      href: '/settings/branches',
      active: currentPath === '/settings/branches',
    },
    {
      id: 'agents',
      label: 'Agents',
      href: '/agents',
      active: currentPath.startsWith('/agents'),
    },
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.user.type === 'auditor'
      ? [
          {
            id: 'audit',
            label: 'Audit Log',
            href: '/audit',
            active: currentPath.startsWith('/audit'),
          },
          {
            id: 'auth-logs',
            label: 'Auth Logs',
            href: '/logs/auth',
            active: currentPath.startsWith('/logs/auth'),
          },
          {
            id: 'audit-notifications',
            label: 'Notification Logs',
            href: '/logs/notifications',
            active: currentPath.startsWith('/logs/notifications'),
          },
        ]
      : []),
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.permissions?.includes('admin.staff.read')
      ? [
          {
            id: 'staff',
            label: 'Staff Management',
            href: '/admin/staff',
            active: currentPath.startsWith('/admin/staff'),
          },
        ]
      : []),
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.permissions?.includes('admin.health.read')
      ? [
          {
            id: 'admin-health',
            label: 'System Health',
            href: '/admin/health',
            active: currentPath.startsWith('/admin/health'),
          },
        ]
      : []),
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.permissions?.includes('admin.maintenance.write')
      ? [
          {
            id: 'admin-maintenance',
            label: 'Maintenance Mode',
            href: '/admin/maintenance',
            active: currentPath.startsWith('/admin/maintenance'),
          },
        ]
      : []),
    ...(session.user.type === 'company_admin' || session.user.type === 'group_admin' || session.permissions?.includes('admin.config.read')
      ? [
          {
            id: 'admin-config',
            label: 'System Config',
            href: '/admin/config',
            active: currentPath.startsWith('/admin/config'),
          },
        ]
      : []),
    {
      id: 'settings-password',
      label: 'Password Settings',
      href: '/settings/password',
      active: currentPath.startsWith('/settings/password'),
    },
    {
      id: 'settings-security',
      label: 'Two-Factor Auth',
      href: '/settings/security',
      active: currentPath.startsWith('/settings/security'),
    },
    {
      id: 'settings-sessions',
      label: 'Active Sessions',
      href: '/settings/sessions',
      active: currentPath.startsWith('/settings/sessions'),
    },
    {
      id: 'settings-fee-slabs',
      label: 'Fee Slabs',
      href: '/settings/fee-slabs',
      active: currentPath.startsWith('/settings/fee-slabs'),
    },
    {
      id: 'settings-areas',
      label: 'Area & Area Groups',
      href: '/settings/areas',
      active: currentPath.startsWith('/settings/areas'),
    },
    {
      id: 'settings-holidays',
      label: 'Holidays',
      href: '/settings/holidays',
      active: currentPath.startsWith('/settings/holidays'),
    },
    {
      id: 'settings-devices',
      label: 'Devices',
      href: '/settings/devices',
      active: currentPath.startsWith('/settings/devices'),
    },
    {
      id: 'settings-financial-year',
      label: 'Financial Year',
      href: '/accounting/periods',
      active: currentPath.startsWith('/accounting/periods'),
    },
  ];

  const navItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <DashboardIcon />,
      href: '/',
      active: currentPath === '/',
    },
    ...(crmItems.length > 0
      ? [
          {
            id: 'crm',
            label: 'CRM',
            icon: (
              <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
                <circle cx="5" cy="4" r="2.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M1 12c0-2 1.8-3.5 4-3.5s4 1.5 4 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="10.5" cy="4.5" r="2" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M13 12c0-1.5-1.1-2.8-2.5-3.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            ),
            subItems: crmItems,
          },
        ]
      : []),
    ...(chitCoreItems.length > 0
      ? [
          {
            id: 'chit-core',
            label: 'Chit Core',
            icon: (
              <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M7 1.5V7M7 7l4 2.5M7 7l-4 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            ),
            subItems: chitCoreItems,
          },
        ]
      : []),
    ...(financeItems.length > 0
      ? [
          {
            id: 'finance',
            label: 'Transactions & Finance',
            icon: (
              <svg className="w-5 h-5" viewBox="0 0 14 14" fill="none">
                <rect x="1.5" y="2" width="11" height="10" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                <path d="M7 2v10M1.5 7h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            ),
            subItems: financeItems,
          },
        ]
      : []),
    ...(['group_admin', 'company_admin', 'staff'].includes(session.user.type) && reportItems.length > 0
      ? [
          {
            id: 'reports-folder',
            label: 'Reports',
            icon: <ReportsIcon />,
            subItems: reportItems,
          },
        ]
      : []),
    ...(configItems.length > 0
      ? [
          {
            id: 'configuration',
            label: 'Settings & Config',
            icon: <SettingsIcon />,
            subItems: configItems,
          },
        ]
      : []),
    {
      id: 'mobile-apps',
      label: 'Mobile Apps',
      icon: <MobileAppsIcon />,
      href: '/apps',
      active: currentPath === '/apps',
    },
  ];

  return (
    <>
      <ConfirmDialog
        open={logoutConfirmOpen}
        title="Sign Out"
        description="Are you sure you want to sign out? Any unsaved changes will be lost."
        confirmLabel="Sign Out"
        onConfirm={handleLogoutConfirmed}
        onCancel={() => setLogoutConfirmOpen(false)}
      />
      <TwoFactorStepUpDialog />
      <AppShell
        navItems={navItems}
        userName={session.user.name || session.user.email}
        userAvatar={session.user.image}
        userEmail={session.user.email}
        userRole={session.user.type}
        onNavigate={(href) => void navigate({ to: href as never })}
        onLogout={() => void handleLogoutClick()}
      >
      <div className="kraphene-fade-in">
        <Outlet />
      </div>
    </AppShell>
    </>
  );
}
